import React from 'react';

import { QUESTION_TYPE } from 'types/question.types';

import { SingleChoiceForm } from '../forms/SingleChoiceForm';
import { LongAnswerForm } from '../forms/LongAnswerForm';
import { CheckboxForm } from '../forms/CheckboxForm';
import { LinearScaleForm } from '../forms/LinearScaleForm';
import {
  SingleChoiceSchema,
  SingleChoiceFormValues,
  SINGLE_CHOICE_DEFAULTS,
  SingleChoiceFormInput
} from '../schemas/SingleChoiceSchema';
import {
  LongAnswerSchema,
  LongAnswerFormValues,
  LONG_ANSWER_DEFAULTS
} from '../schemas/LongAnswerSchema';
import {
  CheckboxSchema,
  CHECKBOX_DEFAULTS,
  CheckboxFormValues,
  CheckboxFormInput
} from '../schemas/CheckboxSchema';
import {
  LinearScaleSchema,
  NPSSchema,
  SatisfactionSchema,
  ConnectionSchema,
  RetentionSchema,
  LinearScaleFormValues,
  NPSFormValues,
  SatisfactionFormValues,
  ConnectionFormValues,
  RetentionFormValues,
  LINEAR_SCALE_DEFAULTS,
  NPS_DEFAULTS,
  SATISFACTION_DEFAULTS,
  CONNECTION_DEFAULTS,
  RETENTION_DEFAULTS
} from '../schemas/LinearScaleSchema';

/**
 * Helper function to convert options from DB format (string[]) to form format ({ value: string }[])
 */
const convertOptionsFromDB = (values: { options: string[] }): { options: { value: string }[] } => ({
  ...values,
  options: values.options.map((value: string) => ({ value }))
});

type QuestionFormTypes =
  | LongAnswerFormValues
  | LinearScaleFormValues
  | NPSFormValues
  | SatisfactionFormValues
  | ConnectionFormValues
  | RetentionFormValues;

// Union type of all possible form values
export type QuestionFormValues = QuestionFormTypes | SingleChoiceFormValues | CheckboxFormValues;

export type QuestionFormInput = QuestionFormTypes | SingleChoiceFormInput | CheckboxFormInput;

type QuestionConfigItem<T extends QuestionFormValues> = {
  schema: any;
  component: React.FC<{ disabled?: boolean }>;
  defaultValues: T;
  convertValuesFromDB?: (values: any) => any;
};

export type QuestionConfig = {
  [QUESTION_TYPE.RADIO]: QuestionConfigItem<SingleChoiceFormValues>;
  [QUESTION_TYPE.LONG]: QuestionConfigItem<LongAnswerFormValues>;
  [QUESTION_TYPE.CHECK]: QuestionConfigItem<CheckboxFormValues>;
  [QUESTION_TYPE.LINEAR_SCALE]: QuestionConfigItem<LinearScaleFormValues>;
  [QUESTION_TYPE.NPS]: QuestionConfigItem<NPSFormValues>;
  [QUESTION_TYPE.SATISFACTION]: QuestionConfigItem<SatisfactionFormValues>;
  [QUESTION_TYPE.CONNECTION]: QuestionConfigItem<ConnectionFormValues>;
  [QUESTION_TYPE.RETENTION]: QuestionConfigItem<RetentionFormValues>;
};

export const QUESTION_CONFIG: QuestionConfig = {
  [QUESTION_TYPE.RADIO]: {
    schema: SingleChoiceSchema,
    component: SingleChoiceForm,
    defaultValues: SINGLE_CHOICE_DEFAULTS,
    convertValuesFromDB: convertOptionsFromDB
  },
  [QUESTION_TYPE.LONG]: {
    schema: LongAnswerSchema,
    component: LongAnswerForm,
    defaultValues: LONG_ANSWER_DEFAULTS
  },
  [QUESTION_TYPE.CHECK]: {
    schema: CheckboxSchema,
    component: CheckboxForm,
    defaultValues: CHECKBOX_DEFAULTS,
    convertValuesFromDB: convertOptionsFromDB
  },
  [QUESTION_TYPE.LINEAR_SCALE]: {
    schema: LinearScaleSchema,
    component: LinearScaleForm,
    defaultValues: LINEAR_SCALE_DEFAULTS
  },
  [QUESTION_TYPE.NPS]: {
    schema: NPSSchema,
    component: LinearScaleForm,
    defaultValues: NPS_DEFAULTS
  },
  [QUESTION_TYPE.SATISFACTION]: {
    schema: SatisfactionSchema,
    component: LinearScaleForm,
    defaultValues: SATISFACTION_DEFAULTS
  },
  [QUESTION_TYPE.CONNECTION]: {
    schema: ConnectionSchema,
    component: LinearScaleForm,
    defaultValues: CONNECTION_DEFAULTS
  },
  [QUESTION_TYPE.RETENTION]: {
    schema: RetentionSchema,
    component: LinearScaleForm,
    defaultValues: RETENTION_DEFAULTS
  }
};
